import nacl from 'tweetnacl';
import naclutil from 'tweetnacl-util';
import { MD5, SHA512 } from 'crypto-js';

export default {
	/**
	 * Serialize object to JSON string without empties
	 *
	 * @param value
	 * @return
	 */
	objectToJson(value: any, ignore: string[] = []): string {
		const filtered = { ...value };

		for (var propName in filtered) {
			if (filtered[propName] === null || filtered[propName] === undefined || ignore.includes(propName)) {
				delete filtered[propName];
			}
		}

		return JSON.stringify(filtered);
	},

	/**
	 * Helper to hash object by algorithm
	 *
	 * @param algorithm
	 * @param value
	 * @return
	 */
	hash(algorithm: string, value: string): string {
		switch (algorithm) {
			case 'MD5':
				return MD5(value).toString();

			case 'SHA-512':
				return SHA512(value).toString();

			default:
				return '';
		}
	},

	/**
	 * Trim a string/null value
	 *
	 * @param value
	 * @return
	 */
	trimValue(value: string | null | undefined): string | null {
		if (value) {
			return value.trim();
		}

		return null;
	},

	/**
	 * Clean an string of whitespaces.
	 *
	 * @param value
	 * @returns string | null
	 */
	cleanString(value: string | null | undefined): string | null {
		if (value) {
			return value.replace(/\s/g, '');
		}

		return null;
	},

	/**
	 * Parse or nullify amount data
	 *
	 * @param amount
	 * @return
	 */
	parseAmount(amount: number | null | undefined): string | null {
		if (typeof amount == 'number' && amount > 0) {
			return String(amount);
		}

		return null;
	},

	/**
	 * Generate encryption key pair
	 *
	 * @return
	 */
	getKeyPair(): nacl.BoxKeyPair {
		return nacl.box.keyPair();
	},

	/**
	 * Encrypt message, returns encrypted value concatenated with base64 nonce.
	 *
	 * @param message
	 * @param publicKey
	 * @param keyPair
	 * @return
	 */
	encrypt(message: string | null, publicKey: string | null, keyPair: nacl.BoxKeyPair): string | null {
		if (!message || !publicKey) {
			return null;
		}

		if (message.includes('|')) {
			return message;
		}

		const nonce = nacl.randomBytes(24);

		const merchantPublicKey = naclutil.decodeBase64(publicKey);
		const sharedKey = nacl.box.before(merchantPublicKey, keyPair.secretKey);

		const box = nacl.box.after(naclutil.decodeUTF8(message), nonce, sharedKey);

		const boxb64 = naclutil.encodeBase64(box);
		const nonceb64 = naclutil.encodeBase64(nonce);

		return `${boxb64}|${nonceb64}`;
	},

	/**
	 * Verify if environment is a Browser
	 */
	checkIsBrowser(): boolean {
		return typeof window !== 'undefined' && typeof window.document !== 'undefined';
	},

	/**
	 * Serialize data instances to a parseable object.
	 *
	 * @param data
	 */
	serialize(data: any): any {
		const properties = Object.getOwnPropertyNames(data ?? {});

		return JSON.parse(JSON.stringify(data, properties));
	},

	/**
	 * Safely parse a potential plain JSON object.
	 *
	 * @param data
	 */
	safeParse(data: any): any|null {
		if (typeof data === 'object') {
			return data;
		}

		try {
			return JSON.parse(data);
		} catch (error) {
			return null;
		}
	},

	/**
	 * Append website script and load
	 *
	 * @param src
	 */
	addScriptToWebsite(src: string): Promise<any> {
		return new Promise((resolve, reject) => {
			const id = MD5(src).toString();
			const script = document.createElement('script');

			if (!document.querySelector(`script[id="${id}"]`)) {
				script.id = id;
				script.src = src;
				script.type = 'text/javascript';
				script.onload = resolve;
				script.onerror = reject;

				document.body.appendChild(script);
			} else {
				resolve(true);
			}
		});
	},

	/**
	 * Append website style
	 *
	 * @param id
	 * @param css
	 */
	addStylesById(id: string, css: string) {
		const el = document.getElementById(`styles-${id}`);

		if (el) {
			return;
		}

		const wrap = document.createElement('style');
		wrap.id = `styles-${id}`;
		wrap.innerHTML = css;

		document.head.appendChild(wrap);
	},

	/**
	 * Append secure enrollment form to site
	 *
	 * @param jwt
	 * @param url
	 */
	addSecureForm(jwt: string, url: string): HTMLFormElement {
		const iframe = document.createElement('iframe');
		iframe.id = 'cardinal_collection_iframe';
		iframe.name = 'collectionIframe';
		iframe.height = '10';
		iframe.width = '10';
		iframe.style.display = 'none';
		document.body.appendChild(iframe);

		const form = document.createElement('form');
		form.id = 'cardinal_collection_form';
		form.method = 'POST';
		form.target = 'collectionIframe';
		form.action = url;

		const input = document.createElement('input');
		input.type = 'hidden';
		input.name = 'JWT';
		input.value = jwt;
		form.appendChild(input);

		document.body.appendChild(form);

		return form;
	},

	/**
	 * Remove HTML Element by ID if it exists.
	 *
	 * @param id
	 */
	removeElementById(id: string) {
		const element = document.getElementById(id);

		if (element) {
			element.remove();
		}
	},

	/**
	 * Convert Window Size to Dimensions
	 */
	convertWindowSizeToDimensions(windowSize: string | null): { width: string; height: string } {
		const dimensions: { [key: string]: { width: string; height: string } } = {
			'01': { width: '250', height: '400' },
			'02': { width: '390', height: '400' },
			'03': { width: '500', height: '600' },
			'04': { width: '600', height: '400' },
			'05': { width: '100%', height: '100%' },
		};

		return dimensions[windowSize || '02'] || { width: '390', height: '400' };
	},
}
